import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IronSightMark from '../../shared/IronSightMark';
import { formatCurrency, formatNumber, formatDate } from '../../../utils/formatting';
import { MarketPhotosBadge, PeerPhotosBadge, PeerQuotesBadge } from './CompsPeerPopover';
import {
    MARKET_EMPTY_TEXT, MARKET_FOOTNOTE, MARKET_SORT_OPTIONS, MARKET_UNAVAILABLE_TEXT, MARKET_MAX_ROWS, MARKET_THIN_COUNT, SEPHR_NOTE,
    currencyOf, excludeSubject, filterSoldComps, peerSummaries, fmtAskDelta, fmtCompactUsd, listedLabel, marketStatus, meterTypeSuffix,
    railLabelStyle, soldSummary, useMarketComps, useMarketExplorer,
} from '../../../utils/marketComps';
import './FastTrackAgentTab.css';
import './IronSightCards.css';

// ── Thinking loader ───────────────────────────────────────────────────────────

export const COMPS_THINKING_STEPS = [
    { label: 'Fetching active peers', detail: 'Same make/model/year in stock' },
    { label: 'Loading sold history', detail: 'Previously sold units, prices, timelines' },
    { label: 'Computing peer summaries', detail: 'Hours, listing duration, sell speed' },
];

export const COMPS_STEP_DURATIONS = [1200, 1000, 99999];

function CompsThinking({ unit }) {
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (step >= COMPS_THINKING_STEPS.length - 1) return;
        const t = setTimeout(() => setStep(s => s + 1), COMPS_STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [step]);

    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');

    return (
        <div className="tab-content">
            <div className="fta-thinking-card">
                <div className="fta-thinking-header">
                    <div className="fta-thinking-pulse" />
                    <div>
                        <div className="fta-thinking-title">Loading comparables</div>
                        {unitLabel && <div className="fta-thinking-unit">{unitLabel}</div>}
                    </div>
                </div>
                <div className="fta-thinking-steps">
                    {COMPS_THINKING_STEPS.map((s, i) => {
                        const state = i < step ? 'done' : i === step ? 'active' : 'pending';
                        return (
                            <div key={i} className={`fta-thinking-step fta-thinking-step--${state}`}>
                                <div className="fta-thinking-step-icon">
                                    {state === 'done'
                                        ? <span className="fta-thinking-check">&#10003;</span>
                                        : state === 'active'
                                            ? <span className="fta-thinking-spinner" />
                                            : <span className="fta-thinking-dot" />}
                                </div>
                                <div className="fta-thinking-step-body">
                                    <span className="fta-thinking-step-label">{s.label}</span>
                                    {state !== 'pending' && (
                                        <span className="fta-thinking-step-detail">{s.detail}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Market Listings ───────────────────────────────────────────────────────────

function MarketListingsSection({ unit, market, loading = false }) {
    // Explorer state and the band / verdict / table view (utils/marketComps,
    // shared with the full unit page). Keyed by stock number by the caller,
    // so it resets per unit.
    const explorer = useMarketExplorer(unit, market);
    const {
        setListingsOpen, setShowAll, showAll, yearsOff, toggleYear, toggleState,
        hoursMinText, setHoursMinText, hoursMaxText, setHoursMaxText,
        stateSel, statesOpen, setStatesOpen, stateFilterRef, stateBtnRef, selectAllStates, selectNoStates,
        sortMode, setSortMode, clearFilters,
    } = explorer;
    const status = marketStatus(market, loading);

    // Still in flight (or not started yet on first render) — show the shell.
    if (status === 'loading') {
        return (
            <section className="detail-section is-card">
                <div className="is-card-body">
                    <h3 className="is-section-title">Market Listings</h3>
                    <div className="market-loading">
                        <span className="fta-thinking-spinner" />
                        <span>Loading market listings…</span>
                    </div>
                </div>
            </section>
        );
    }

    // No market coverage for this unit — render nothing at all.
    if (status === 'hidden') return null;

    if (status === 'unavailable') {
        return (
            <section className="detail-section is-card">
                <div className="is-card-body">
                    <h3 className="is-section-title">Market Listings</h3>
                    <p className="pipeline-empty market-unavailable">
                        {MARKET_UNAVAILABLE_TEXT}
                    </p>
                </div>
            </section>
        );
    }

    if (status === 'empty') {
        return (
            <section className="detail-section is-card">
                <div className="is-card-body">
                    <h3 className="is-section-title">Market Listings</h3>
                    <p className="pipeline-empty">{MARKET_EMPTY_TEXT}</p>
                </div>
            </section>
        );
    }

    const {
        listings, ask, aiPrice, distinctYears, stateEntries, stateFilterActive, filtersActive, matching, visible,
        collapsedCount, usdCount, thinMarket, expanded, showRail, priceMin, priceP25, priceMedian, priceP75, priceMax,
        p25Pos, p75Pos, medianPos, showP25Label, showP75Label, askRaw, askPos, aiRaw, aiPos, aiLabelOffset,
        verdict, recordCount, contextParts, subParts, visibleHasSephr,
    } = explorer.view;

    return (
        <section className="detail-section is-card">
            <div className="is-card-head">
                <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                <span className="is-card-title">Market Position</span>
                <span className="market-head-sub">{subParts.join(' · ')}</span>
            </div>
            <div className="is-card-body">
                {thinMarket ? (
                    <p className="market-thin">
                        {filtersActive ? (
                            // Same thin-market rule, filtered basis: too few
                            // filtered USD asks for a market statement.
                            <>
                                {usdCount > 0 ? (
                                    <>
                                        Only {formatNumber(usdCount)} comparable
                                        {usdCount === 1 ? ' machine matches' : ' machines match'} your filters
                                        {' — '}thin market, individual listings below.
                                    </>
                                ) : (
                                    'No USD-priced listings match your filters.'
                                )}
                                {' '}
                                <button
                                    type="button"
                                    className="market-filter-clear"
                                    onClick={clearFilters}
                                >
                                    Show full market
                                </button>
                            </>
                        ) : usdCount > 0 && usdCount < MARKET_THIN_COUNT ? (
                            <>
                                Only {formatNumber(usdCount)} comparable machines listed market-wide
                                {' — '}thin market, individual listings below.
                            </>
                        ) : (
                            'Market price statistics unavailable for this model — individual listings below.'
                        )}
                    </p>
                ) : (
                    <>
                        <div className="market-ask-row">
                            {ask != null && (
                                <div className="market-ask">
                                    <span className="market-ask-eyebrow">Your ask</span>
                                    <span className="market-ask-value">{formatCurrency(ask)}</span>
                                </div>
                            )}
                            <div className="market-ask-right">
                                {aiPrice != null && (
                                    <div className="market-ask market-ask--ai">
                                        <span className="market-ask-eyebrow">IronSight estimate</span>
                                        <span className="market-ask-value market-ask-value--ai">
                                            <span className="market-ask-ai-glyph" aria-hidden="true">◇</span>
                                            {formatCurrency(aiPrice)}
                                        </span>
                                    </div>
                                )}
                                <span className="market-ask-vs">
                                    vs {formatNumber(usdCount)} comparable listings
                                </span>
                            </div>
                        </div>

                        {showRail && (
                            <div className="market-rail-wrap">
                                <div className="market-median-row">
                                    <span className="market-median-label" style={railLabelStyle(medianPos)}>
                                        median {fmtCompactUsd(priceMedian)}
                                    </span>
                                </div>
                                {aiPos != null && (
                                    <div className="market-ai-row">
                                        <span
                                            className={`market-ai-label${aiLabelOffset ? ' market-ai-label--offset' : ''}`}
                                            style={railLabelStyle(aiPos)}
                                        >
                                            {aiRaw < 0 && <span className="market-offscale market-offscale--ai">◂</span>}
                                            IronSight {fmtCompactUsd(aiPrice)}
                                            {aiRaw > 100 && <span className="market-offscale market-offscale--ai">▸</span>}
                                        </span>
                                    </div>
                                )}
                                <div className="market-rail">
                                    <div
                                        className="market-rail-zone market-rail-zone--low"
                                        style={{ width: `${p25Pos}%` }}
                                    >
                                        <span className="market-rail-zone-label">Low</span>
                                    </div>
                                    <div
                                        className="market-rail-zone market-rail-zone--typical"
                                        style={{ width: `${p75Pos - p25Pos}%` }}
                                    >
                                        <span className="market-rail-zone-label">Typical</span>
                                    </div>
                                    <div
                                        className="market-rail-zone market-rail-zone--high"
                                        style={{ width: `${100 - p75Pos}%` }}
                                    >
                                        <span className="market-rail-zone-label">High</span>
                                    </div>
                                    <span className="market-rail-median" style={{ left: `${medianPos}%` }} />
                                    {askPos != null && (
                                        <span className="market-rail-yours" style={{ left: `${askPos}%` }} />
                                    )}
                                    {aiPos != null && (
                                        /* Hollow ink diamond riding the TOP edge —
                                           visually distinct from the sage YOURS
                                           dot-and-line below the rail. */
                                        <span className="market-rail-ai" style={{ left: `${aiPos}%` }} />
                                    )}
                                </div>
                                <div className="market-rail-scale">
                                    <span className="market-scale-label market-scale-label--min">
                                        {fmtCompactUsd(priceMin)}
                                    </span>
                                    {showP25Label && (
                                        <span className="market-scale-label" style={railLabelStyle(p25Pos)}>
                                            {fmtCompactUsd(priceP25)}
                                        </span>
                                    )}
                                    {showP75Label && (
                                        <span className="market-scale-label" style={railLabelStyle(p75Pos)}>
                                            {fmtCompactUsd(priceP75)}
                                        </span>
                                    )}
                                    <span className="market-scale-label market-scale-label--max" style={{ left: '100%' }}>
                                        {fmtCompactUsd(priceMax)}
                                    </span>
                                </div>
                                {askPos != null && (
                                    <div className="market-yours-row">
                                        <span
                                            className="market-yours-label"
                                            style={railLabelStyle(askPos)}
                                        >
                                            {askRaw < 0 && <span className="market-offscale">◂</span>}
                                            YOURS {fmtCompactUsd(ask)}
                                            {askRaw > 100 && <span className="market-offscale">▸</span>}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {filtersActive && (
                            /* Honesty badge: the band above no longer speaks
                               for the full market. */
                            <div className="market-filter-badge">
                                Based on {formatNumber(usdCount)} filtered listing{usdCount === 1 ? '' : 's'} —{' '}
                                <button
                                    type="button"
                                    className="market-filter-clear"
                                    onClick={clearFilters}
                                >
                                    Show full market
                                </button>
                            </div>
                        )}

                        {verdict && (
                            <p className="is-rec market-verdict">
                                {verdict.pill && (
                                    <span className={`market-verdict-pill market-verdict-pill--${verdict.pill.toLowerCase()}`}>
                                        {verdict.pill}
                                    </span>
                                )}
                                <span className="is-rec-lead">{verdict.lead}</span>
                                {verdict.body}
                            </p>
                        )}

                        {contextParts.length > 0 && (
                            <div className="market-context">{contextParts.join(' · ')}</div>
                        )}
                    </>
                )}

                <button
                    type="button"
                    className="market-toggle"
                    aria-expanded={expanded}
                    onClick={() => setListingsOpen(!expanded)}
                >
                    {expanded ? '▾ Hide listings' : `▸ Show the ${collapsedCount} latest listings`}
                </button>

                {expanded && (
                    <>
                        <div className="market-filter-row">
                            {distinctYears.length > 0 && (
                                <div className="market-filter-years" role="group" aria-label="Filter by model year">
                                    {distinctYears.map((year) => {
                                        const active = !yearsOff.has(year);
                                        return (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`market-filter-chip${active ? ' market-filter-chip--active' : ''}`}
                                                aria-pressed={active}
                                                onClick={() => toggleYear(year)}
                                            >
                                                {year}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="market-filter-hours">
                                <span className="market-filter-label">Hours</span>
                                <input
                                    className="market-filter-input"
                                    inputMode="numeric"
                                    placeholder="min"
                                    aria-label="Minimum hours"
                                    value={hoursMinText}
                                    onChange={(event) => setHoursMinText(event.target.value)}
                                />
                                <span className="market-filter-dash">–</span>
                                <input
                                    className="market-filter-input"
                                    inputMode="numeric"
                                    placeholder="max"
                                    aria-label="Maximum hours"
                                    value={hoursMaxText}
                                    onChange={(event) => setHoursMaxText(event.target.value)}
                                />
                            </div>
                            <div className="market-state-filter" ref={stateFilterRef}>
                                <button
                                    ref={stateBtnRef}
                                    type="button"
                                    className={`market-state-btn${stateFilterActive ? ' market-state-btn--active' : ''}`}
                                    aria-haspopup="dialog"
                                    aria-expanded={statesOpen}
                                    onClick={() => setStatesOpen((open) => !open)}
                                >
                                    States · {stateFilterActive ? stateSel.size : 'All'}
                                </button>
                                {statesOpen && (
                                    <div className="market-state-pop" role="group" aria-label="Filter by state">
                                        <div className="market-state-actions">
                                            <button
                                                type="button"
                                                className="market-state-action"
                                                onClick={selectAllStates}
                                            >
                                                All
                                            </button>
                                            <button
                                                type="button"
                                                className="market-state-action"
                                                onClick={selectNoStates}
                                            >
                                                None
                                            </button>
                                        </div>
                                        {stateEntries.map(({ key, label, count }) => (
                                            <label key={key} className="market-state-row">
                                                <input
                                                    type="checkbox"
                                                    checked={stateSel == null || stateSel.has(key)}
                                                    onChange={() => toggleState(key)}
                                                />
                                                <span className="market-state-name">{label}</span>
                                                {' '}
                                                <span className="market-state-count">({count})</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <select
                                className="market-filter-select"
                                aria-label="Sort listings"
                                value={sortMode}
                                onChange={(event) => setSortMode(event.target.value)}
                            >
                                {MARKET_SORT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="comps-table-wrap">
                        <table className="comps-table">
                            <thead>
                                <tr>
                                    <th>Year</th>
                                    <th>Ask Price</th>
                                    <th>vs Ours</th>
                                    <th>Hours</th>
                                    <th>State</th>
                                    <th>Listed</th>
                                    <th>Photos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matching.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="market-filter-empty">
                                            No listings match —{' '}
                                            <button
                                                type="button"
                                                className="market-filter-clear"
                                                onClick={clearFilters}
                                            >
                                                clear filters
                                            </button>
                                        </td>
                                    </tr>
                                ) : visible.map((listing) => {
                                    const suffix = meterTypeSuffix(listing.meter_type);
                                    const photoCount = listing.image_count ?? 0;
                                    return (
                                        <tr key={listing._servedIndex} title={listing.features_text || undefined}>
                                            <td>{listing.year ?? '—'}</td>
                                            <td className="market-ask-cell">
                                                {formatCurrency(listing.price)}
                                                {currencyOf(listing) === 'CAD' && (
                                                    <span className="market-cad-badge">CAD</span>
                                                )}
                                            </td>
                                            <td className="market-delta-cell">
                                                {/* A CAD ask against our USD price is an FX lie — no delta. */}
                                                {currencyOf(listing) === 'CAD' ? '—' : fmtAskDelta(listing.price, ask)}
                                            </td>
                                            <td>
                                                {listing.meter_hours != null ? formatNumber(listing.meter_hours) : '—'}
                                                {listing.meter_hours != null && suffix && (
                                                    <span className="market-meter-type">{suffix}</span>
                                                )}
                                            </td>
                                            <td>{listing.state ?? '—'}</td>
                                            <td>{listedLabel(listing)}</td>
                                            <td>
                                                {photoCount === 0 ? (
                                                    <span className="market-photos-dash">—</span>
                                                ) : (
                                                    /* _servedIndex — never the visual row index —
                                                       maps 1:1 onto the backend photo proxy's
                                                       listing_index into the served array, so
                                                       photos survive any client-side sort/filter. */
                                                    <MarketPhotosBadge
                                                        stockNumber={String(unit.StockNumber)}
                                                        listingIndex={listing._servedIndex}
                                                        count={photoCount}
                                                        badgeClass="market-photos-neutral"
                                                        buildId={explorer.view.buildId}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {!showAll && matching.length > MARKET_MAX_ROWS && (
                            <div className="market-show-all-row">
                                <button
                                    type="button"
                                    className="market-show-all"
                                    onClick={() => setShowAll(true)}
                                >
                                    Show all {matching.length} matching listings
                                </button>
                            </div>
                        )}
                        </div>
                    </>
                )}

                {expanded && filtersActive && (
                    <div className="market-showing market-filter-summary">
                        {matching.length} of {listings.length} listings match your filters
                        {recordCount > listings.length && ` · ${formatNumber(recordCount)} in market`}
                    </div>
                )}
                {expanded && thinMarket && !filtersActive && (
                    <div className="market-showing">
                        Showing {visible.length} of {formatNumber(recordCount)} listings
                    </div>
                )}

                <p className="market-footnote">
                    {MARKET_FOOTNOTE}
                    {visibleHasSephr && ` ${SEPHR_NOTE}`}
                </p>
            </div>
        </section>
    );
}

MarketListingsSection.propTypes = {
    unit: PropTypes.object.isRequired,
    market: PropTypes.shape({
        available: PropTypes.bool,
        reason: PropTypes.string,
        summary: PropTypes.object,
        listings: PropTypes.array,
    }),
    loading: PropTypes.bool,
};

// ── Main component ────────────────────────────────────────────────────────────

function CompsTab({ unit, comps, compsLoading }) {
    const [soldYearRange, setSoldYearRange] = useState(1);

    // Market listings are fetched lazily, right here, because CompsTab only
    // mounts while the Comps tab is active (utils/marketComps useMarketComps:
    // keyed by stock number, abortable).
    const stockNumber = unit.StockNumber;
    // Never the unit itself (QA P-1).
    const peers = excludeSubject(comps?.active_peers, stockNumber);
    const { hours_summary: hoursSummary, listing_duration_summary: listedSummary } = peerSummaries(comps, stockNumber);
    const { market, loading: marketLoading } = useMarketComps(stockNumber);

    if (compsLoading) {
        return <CompsThinking unit={unit} />;
    }

    if (!comps) return null;

    const filteredSold = filterSoldComps(comps.sold_comps, soldYearRange);

    return (
        <>
            <section className="detail-section is-card">
                <div className="is-card-body">
                <h3 className="is-section-title">
                    Active Peers
                    <span className="comps-sub"> — {comps.make} {comps.model} ({comps.year}) currently in stock</span>
                </h3>
                {peers.length === 0 ? (
                    <p className="pipeline-empty">No other units of this make/model/year currently in stock.</p>
                ) : (
                    <>
                        {hoursSummary && (
                            <div className="comps-sold-summary">
                                <span>{peers.length} peer{peers.length === 1 ? '' : 's'}</span>
                                {hoursSummary && (
                                    <>
                                        <span className="comps-sold-sep">·</span>
                                        <span>{formatNumber(Math.round(hoursSummary.avg))}h avg hours</span>
                                    </>
                                )}
                                {listedSummary && (
                                    <>
                                        <span className="comps-sold-sep">·</span>
                                        <span>{Math.round(listedSummary.avg)}d avg listed</span>
                                    </>
                                )}
                            </div>
                        )}
                    <div className="comps-table-wrap">
                        <table className="comps-table">
                            <thead>
                                <tr>
                                    <th>Stock #</th>
                                    <th>Asking Price</th>
                                    <th>Hours</th>
                                    <th>Days in Stock</th>
                                    <th>Branch</th>
                                    <th>Condition</th>
                                    <th>Photos</th>
                                    <th>Quotes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {peers.map((peer) => {
                                    const imageCount = peer.image_count ?? 0;
                                    const imageClass = imageCount === 0 ? 'comps-photos-none' : imageCount < 5 ? 'comps-photos-few' : 'comps-photos-good';
                                    const quoteCount = peer.quote_count ?? 0;
                                    const quoteClass = quoteCount === 0 ? 'comps-photos-none' : quoteCount < 3 ? 'comps-photos-few' : 'comps-photos-good';

                                    return (
                                        <tr key={peer.stock_number} className={peer.stock_number === unit.StockNumber ? 'comps-row-self' : ''}>
                                            <td>#{peer.stock_number}</td>
                                            <td className={peer.asking_price != null && unit.Best_Sell_Price != null
                                                ? peer.asking_price < unit.Best_Sell_Price ? 'comps-val-low' : peer.asking_price > unit.Best_Sell_Price ? 'comps-val-high' : ''
                                                : ''}>
                                                {formatCurrency(peer.asking_price)}
                                            </td>
                                            <td>{peer.hours != null ? formatNumber(peer.hours) : '—'}</td>
                                            <td>{peer.days_in_stock != null ? `${peer.days_in_stock}d` : '—'}</td>
                                            <td>{peer.branch || '—'}</td>
                                            <td>{peer.new_used || '—'}</td>
                                            <td>
                                                <PeerPhotosBadge
                                                    stockNumber={String(peer.stock_number)}
                                                    count={imageCount}
                                                    badgeClass={imageClass}
                                                />
                                            </td>
                                            <td>
                                                <PeerQuotesBadge
                                                    stockNumber={String(peer.stock_number)}
                                                    count={quoteCount}
                                                    badgeClass={quoteClass}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
                </div>
            </section>

            <section className="detail-section is-card">
                <div className="is-card-body">
                <div className="comps-sold-header">
                    <h3 className="is-section-title">
                        Sold Comps
                        <span className="comps-sub"> — previously sold {comps.make} {comps.model} ({comps.year})</span>
                    </h3>
                    <select
                        className="comps-year-filter"
                        value={soldYearRange}
                        onChange={(event) => setSoldYearRange(Number(event.target.value))}
                    >
                        <option value={1}>Last 1 year</option>
                        <option value={2}>Last 2 years</option>
                        <option value={3}>Last 3 years</option>
                        <option value={0}>All time</option>
                    </select>
                </div>
                {filteredSold.length > 0 && (() => {
                    const stats = soldSummary(filteredSold);
                    if (!stats) return null;
                    const { count, avg, min, max } = stats;
                    return (
                        <div className="comps-sold-summary">
                            <span>{count} sold</span>
                            <span className="comps-sold-sep">·</span>
                            <span>{avg}d avg</span>
                            <span className="comps-sold-sep">·</span>
                            <span>{min}d fastest</span>
                            <span className="comps-sold-sep">·</span>
                            <span>{max}d slowest</span>
                        </div>
                    );
                })()}
                {filteredSold.length === 0 ? (
                    <p className="pipeline-empty">No sold units found for the selected period.</p>
                ) : (
                    <div className="comps-table-wrap">
                        <table className="comps-table">
                            <thead>
                                <tr>
                                    <th>Stock #</th>
                                    <th>Sale Price</th>
                                    <th>Hours</th>
                                    <th>Days to Sell</th>
                                    <th>Sold Date</th>
                                    <th>Branch</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSold.map((sold) => (
                                    <tr key={sold.stock_number}>
                                        <td>#{sold.stock_number}</td>
                                        <td>{formatCurrency(sold.sale_price)}</td>
                                        <td>{sold.hours != null ? formatNumber(sold.hours) : '—'}</td>
                                        <td>{sold.days_to_sell != null ? `${sold.days_to_sell}d` : '—'}</td>
                                        <td>{sold.sold_date ? formatDate(sold.sold_date) : '—'}</td>
                                        <td>{sold.branch || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
            </section>

            {Boolean(stockNumber) && (
                <MarketListingsSection
                    key={stockNumber}
                    unit={unit}
                    market={market}
                    loading={marketLoading}
                />
            )}
        </>
    );
}

CompsTab.propTypes = {
    unit: PropTypes.object.isRequired,
    comps: PropTypes.shape({
        make: PropTypes.string,
        model: PropTypes.string,
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        active_peers: PropTypes.array,
        sold_comps: PropTypes.array,
    }),
    compsLoading: PropTypes.bool,
};

CompsTab.defaultProps = {
    comps: null,
    compsLoading: false,
};

export default CompsTab;
